import { useState } from 'react';
import { useGetCatalogSections } from '../../../../../../services/modules/catalogs/catalogs.queries';
import { CatalogViewGroupBy } from '../../view/catalogView.types';
import {
  CatalogMoveToSectionRow,
  UseCatalogMoveToSectionModalParams,
} from './catalogMoveToSectionModal.types';

export const DRAFT_SECTION_ID = 'draft-section';

const normalizeSectionName = (name: string) => name.trim().toLowerCase();

export const useCatalogMoveToSectionModal = ({
  isOpen,
  catalogId,
  onConfirm,
}: UseCatalogMoveToSectionModalParams) => {
  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const [draftSectionName, setDraftSectionName] = useState<string>();
  const [draftInputValue, setDraftInputValue] = useState('');
  const [isMoving, setIsMoving] = useState(false);

  const sectionsQuery = useGetCatalogSections(
    {
      id: catalogId,
      filters: {},
      customerIds: [],
      groupParams: { groupBy: CatalogViewGroupBy.MANUAL },
    },
    { enabled: isOpen },
  );

  const sections = sectionsQuery.data?.sections ?? [];

  const rows: CatalogMoveToSectionRow[] = sections.map((section) => ({
    id: section.catalogSectionId,
    name: section.catalogSectionName,
    isNew: false,
  }));

  if (draftSectionName) {
    rows.push({ id: DRAFT_SECTION_ID, name: draftSectionName, isNew: true });
  }

  const typedSectionName = draftInputValue.trim();
  const isNameDuplicated =
    typedSectionName.length > 0 &&
    rows.some((row) => normalizeSectionName(row.name) === normalizeSectionName(typedSectionName));

  const handleCreateDraftSection = () => {
    if (typedSectionName.length === 0 || isNameDuplicated) {
      return;
    }

    setDraftSectionName(typedSectionName);
    setSelectedSectionId(DRAFT_SECTION_ID);
    setDraftInputValue('');
  };

  const resolveTarget = () => {
    if (selectedSectionId === DRAFT_SECTION_ID) {
      return { sectionName: draftSectionName };
    }

    return { sectionId: selectedSectionId };
  };

  const handleConfirm = async () => {
    if (!selectedSectionId) {
      return;
    }

    setIsMoving(true);
    await onConfirm(resolveTarget());
    setIsMoving(false);
  };

  const handleExited = () => {
    setSelectedSectionId(undefined);
    setDraftSectionName(undefined);
    setDraftInputValue('');
  };

  return {
    rows,
    selectedSectionId,
    draftInputValue,
    typedSectionName,
    isNameDuplicated,
    isMoving,
    isCreateDisabled: typedSectionName.length === 0 || isNameDuplicated,
    isConfirmDisabled: !selectedSectionId || isMoving,
    handleSelectSection: setSelectedSectionId,
    handleChangeDraftInput: setDraftInputValue,
    handleCreateDraftSection,
    handleConfirm,
    handleExited,
  };
};
